import { computed, ref } from "vue";

// Generic pagination/search/sort helper that delegates fetching to a provided function.
export function usePaginatedFetcher(
  fetcher,
  {
    getExtraParams,
    initialRows = 10,
    debounceMs = 400,
    accumulate = false,
  } = {}
) {
  const items = ref([]);
  const total = ref(0);
  const page = ref(0);
  const rows = ref(initialRows);
  const sortField = ref(null);
  const sortOrder = ref(null);
  const searchTerm = ref("");
  const loading = ref(false);
  const firstRow = computed(() => page.value * rows.value);

  let searchDebounce = null;

  const fetchPage = async (pageToLoad = page.value) => {
    loading.value = true;
    try {
      const extraParams = (getExtraParams && getExtraParams()) || {};
      const params = {
        page: pageToLoad + 1,
        pageSize: rows.value,
        ...extraParams,
      };

      const trimmedSearch = searchTerm.value.trim();
      if (trimmedSearch) params.search = trimmedSearch;
      if (sortField.value) {
        params.sortField = sortField.value;
        params.sortOrder = sortOrder.value;
      }

      const {
        items: fetchedItems,
        episodes,
        total: fetchedTotal,
      } = (await fetcher(params)) || {};

      const normalizedItems = fetchedItems || episodes || [];
      if (accumulate && pageToLoad > 0) {
        items.value = [...items.value, ...normalizedItems];
      } else {
        items.value = normalizedItems;
      }
      total.value =
        fetchedTotal !== undefined ? fetchedTotal : normalizedItems.length;
      page.value = pageToLoad;
    } catch (error) {
      console.error("Error in paginated fetcher:", error);
      items.value = [];
      total.value = 0;
    } finally {
      loading.value = false;
    }
  };

  const refresh = () => fetchPage(page.value);

  const onPageChange = (event) => {
    rows.value = event.rows;
    sortField.value = event.sortField || sortField.value;
    sortOrder.value = event.sortOrder ?? sortOrder.value;
    fetchPage(event.page);
  };

  const onSort = (event) => {
    sortField.value = event.sortField;
    sortOrder.value = event.sortOrder;
    page.value = 0;
    fetchPage(0);
  };

  const onSearchInput = () => {
    if (searchDebounce) clearTimeout(searchDebounce);
    searchDebounce = setTimeout(() => {
      page.value = 0;
      fetchPage(0);
    }, debounceMs);
  };

  const cleanupSearch = () => {
    if (searchDebounce) clearTimeout(searchDebounce);
  };

  const reset = ({ keepSearch = false } = {}) => {
    items.value = [];
    total.value = 0;
    page.value = 0;
    if (!keepSearch) searchTerm.value = "";
  };

  return {
    items,
    total,
    page,
    rows,
    firstRow,
    sortField,
    sortOrder,
    searchTerm,
    loading,
    fetchPage,
    refresh,
    onPageChange,
    onSort,
    onSearchInput,
    cleanupSearch,
    reset,
  };
}
